import React, { useState, useEffect, useContext } from 'react';
import './RoundTableProfile.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthContext } from '../../context/AuthContext';

interface TreasurerData {
    orgName: string;
    schatzmeisterName: string;
    schatzmeisterRole: string;
    schatzmeisterStreet: string;
    schatzmeisterPostal: string;
    schatzmeisterCity: string;
    schatzmeisterEmail: string;
    schatzmeisterPhone: string;
}

interface BankAccount {
    id: number;
    accountName: string;
    institute: string;
    iban: string;
    bic: string;
    glaeubigerId: string;
}

const RoundTableProfile: React.FC = () => {
    const authContext = useContext(AuthContext);

    if (!authContext) {
        throw new Error('RoundTableProfile must be used within an AuthProvider');
    }

    const { token } = authContext;

    const [treasurer, setTreasurer] = useState<TreasurerData>({
        orgName: '',
        schatzmeisterName: '',
        schatzmeisterRole: '',
        schatzmeisterStreet: '',
        schatzmeisterPostal: '',
        schatzmeisterCity: '',
        schatzmeisterEmail: '',
        schatzmeisterPhone: ''
    });
    const [originalTreasurer, setOriginalTreasurer] = useState<TreasurerData>({ ...treasurer });
    const [isTreasurerChanged, setIsTreasurerChanged] = useState(false);
    const [treasurerLoading, setTreasurerLoading] = useState(true);

    // Bank accounts data - loaded from API
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
    const [originalAccountData, setOriginalAccountData] = useState<{ [key: number]: BankAccount }>({});
    const [isAddingAccount, setIsAddingAccount] = useState(false);
    const [newAccount, setNewAccount] = useState<BankAccount>({
        id: 0,
        accountName: '',
        institute: '',
        iban: '',
        bic: '',
        glaeubigerId: ''
    });

    // Fetch organisation data on component mount
    useEffect(() => {
        const fetchOrganisation = async () => {
            try {
                const response = await fetch('/api/organisation', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch organisation data');
                }

                const data = await response.json();
                
                // Only update if data exists
                if (data && Object.keys(data).length > 0) {
                    const organisationData: TreasurerData = {
                        orgName: data.orgName || '',
                        schatzmeisterName: data.schatzmeisterName || '',
                        schatzmeisterRole: data.schatzmeisterRole || '',
                        schatzmeisterStreet: data.schatzmeisterStreet || '',
                        schatzmeisterPostal: data.schatzmeisterPostal || '',
                        schatzmeisterCity: data.schatzmeisterCity || '',
                        schatzmeisterEmail: data.schatzmeisterEmail || '',
                        schatzmeisterPhone: data.schatzmeisterPhone || ''
                    };
                    setTreasurer(organisationData);
                    setOriginalTreasurer(organisationData);
                }
            } catch (error) {
                console.error('Error fetching organisation:', error);
                toast.error('Fehler beim Laden der Organisationsdaten');
            } finally {
                setTreasurerLoading(false);
            }
        };

        fetchOrganisation();
    }, [token]);

    // Fetch bank accounts on component mount
    useEffect(() => {
        const fetchBankAccounts = async () => {
            try {
                const response = await fetch('/api/bankaccounts', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch bank accounts');
                }

                const data: BankAccount[] = await response.json();
                setBankAccounts(data);
            } catch (error) {
                console.error('Error fetching bank accounts:', error);
                toast.error('Fehler beim Laden der Bankkonten');
            } finally {
                setLoading(false);
            }
        };

        fetchBankAccounts();
    }, [token]);

    const handleTreasurerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setTreasurer((prev) => {
            const updated = { ...prev, [name]: value };
            setIsTreasurerChanged(JSON.stringify(updated) !== JSON.stringify(originalTreasurer));
            return updated;
        });
    };

    const handleTreasurerSave = async () => {
        // Validate mandatory fields
        if (!treasurer.orgName || !treasurer.orgName.trim()) {
            toast.error('Organisation ist ein Pflichtfeld');
            return;
        }
        if (!treasurer.schatzmeisterName || !treasurer.schatzmeisterName.trim()) {
            toast.error('Name ist ein Pflichtfeld');
            return;
        }

        try {
            const response = await fetch('/api/organisation', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    orgName: treasurer.orgName,
                    schatzmeisterName: treasurer.schatzmeisterName,
                    schatzmeisterRole: treasurer.schatzmeisterRole,
                    schatzmeisterStreet: treasurer.schatzmeisterStreet,
                    schatzmeisterPostal: treasurer.schatzmeisterPostal,
                    schatzmeisterCity: treasurer.schatzmeisterCity,
                    schatzmeisterEmail: treasurer.schatzmeisterEmail,
                    schatzmeisterPhone: treasurer.schatzmeisterPhone,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to save organisation data');
            }

            const updatedData = await response.json();
            const updatedTreasurer: TreasurerData = {
                orgName: updatedData.orgName || '',
                schatzmeisterName: updatedData.schatzmeisterName || '',
                schatzmeisterRole: updatedData.schatzmeisterRole || '',
                schatzmeisterStreet: updatedData.schatzmeisterStreet || '',
                schatzmeisterPostal: updatedData.schatzmeisterPostal || '',
                schatzmeisterCity: updatedData.schatzmeisterCity || '',
                schatzmeisterEmail: updatedData.schatzmeisterEmail || '',
                schatzmeisterPhone: updatedData.schatzmeisterPhone || ''
            };
            
            setTreasurer(updatedTreasurer);
            setOriginalTreasurer(updatedTreasurer);
            setIsTreasurerChanged(false);
            toast.success('Schatzmeister-Daten erfolgreich gespeichert');
        } catch (error) {
            console.error('Error saving organisation data:', error);
            toast.error('Fehler beim Speichern der Schatzmeister-Daten');
        }
    };

    const handleTreasurerCancel = () => {
        setTreasurer({ ...originalTreasurer });
        setIsTreasurerChanged(false);
    };

    const handleEditAccount = (accountId: number) => {
        const account = bankAccounts.find(acc => acc.id === accountId);
        if (account) {
            setOriginalAccountData({ ...originalAccountData, [accountId]: { ...account } });
        }
        setEditingAccountId(accountId);
        setIsAddingAccount(false);
    };

    const handleAccountInputChange = (accountId: number, field: keyof BankAccount, value: string) => {
        setBankAccounts((prev) =>
            prev.map((account) =>
                account.id === accountId ? { ...account, [field]: value } : account
            )
        );
    };

    const handleSaveAccount = async (accountId: number) => {
        const account = bankAccounts.find(acc => acc.id === accountId);
        if (!account) return;

        try {
            const response = await fetch(`/api/bankaccounts/${accountId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountName: account.accountName,
                    institute: account.institute,
                    iban: account.iban,
                    bic: account.bic,
                    glaeubigerId: account.glaeubigerId,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update bank account');
            }

            const updatedAccount = await response.json();
            
            // Update the account in the list with the new one from the server
            setBankAccounts(prev => prev.map(acc => acc.id === accountId ? updatedAccount : acc));
            setEditingAccountId(null);
            delete originalAccountData[accountId];
            toast.success('Konto erfolgreich aktualisiert');
        } catch (error) {
            console.error('Error updating bank account:', error);
            toast.error('Fehler beim Aktualisieren des Kontos');
        }
    };

    const handleCancelEditAccount = (accountId: number) => {
        // Restore original account data
        if (originalAccountData[accountId]) {
            setBankAccounts(prev => 
                prev.map(acc => acc.id === accountId ? originalAccountData[accountId] : acc)
            );
            const newOriginalData = { ...originalAccountData };
            delete newOriginalData[accountId];
            setOriginalAccountData(newOriginalData);
        }
        setEditingAccountId(null);
    };

    const handleAddAccount = () => {
        setIsAddingAccount(true);
        setEditingAccountId(null);
        setNewAccount({
            id: 0,
            accountName: '',
            institute: '',
            iban: '',
            bic: '',
            glaeubigerId: ''
        });
    };

    const handleNewAccountInputChange = (field: keyof BankAccount, value: string) => {
        setNewAccount((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveNewAccount = async () => {
        if (!newAccount.accountName || !newAccount.institute || !newAccount.iban || !newAccount.bic || !newAccount.glaeubigerId) {
            toast.error('Bitte füllen Sie alle Felder aus');
            return;
        }

        try {
            const response = await fetch('/api/bankaccounts', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountName: newAccount.accountName,
                    institute: newAccount.institute,
                    iban: newAccount.iban,
                    bic: newAccount.bic,
                    glaeubigerId: newAccount.glaeubigerId,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to create bank account');
            }

            const createdAccount = await response.json();
            setBankAccounts((prev) => [...prev, createdAccount]);
            setIsAddingAccount(false);
            setNewAccount({ id: 0, accountName: '', institute: '', iban: '', bic: '', glaeubigerId: '' });
            toast.success('Konto erfolgreich hinzugefügt');
        } catch (error) {
            console.error('Error creating bank account:', error);
            toast.error('Fehler beim Erstellen des Kontos');
        }
    };

    const handleCancelAddAccount = () => {
        setIsAddingAccount(false);
        setNewAccount({ id: 0, accountName: '', institute: '', iban: '', bic: '', glaeubigerId: '' });
    };

    const handleDeleteAccount = async (accountId: number) => {
        const account = bankAccounts.find(acc => acc.id === accountId);
        if (!account) return;

        // Ask for confirmation
        const confirmMessage = `Möchten Sie das Bankkonto "${account.accountName}" wirklich löschen?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const response = await fetch(`/api/bankaccounts/${accountId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete bank account');
            }

            // Remove the account from the list
            setBankAccounts(prev => prev.filter(acc => acc.id !== accountId));
            toast.success('Konto erfolgreich gelöscht');
        } catch (error) {
            console.error('Error deleting bank account:', error);
            toast.error('Fehler beim Löschen des Kontos');
        }
    };

    return (
        <div className="roundtable-profile-container">
            {/* Treasurer Data Form */}
            <div className="profile-card">
                <h2>Schatzmeister-Daten</h2>
                {treasurerLoading ? (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                        Lade Organisationsdaten...
                    </div>
                ) : (
                    <>
                <div className="profile-inputs">
                    <label>Organisation: *</label>
                    <input
                        type="text"
                        name="orgName"
                        value={treasurer.orgName}
                        onChange={handleTreasurerInputChange}
                        required
                    />
                    <label>Name: *</label>
                    <input
                        type="text"
                        name="schatzmeisterName"
                        value={treasurer.schatzmeisterName}
                        onChange={handleTreasurerInputChange}
                        required
                    />
                    <label>Rolle:</label>
                    <input
                        type="text"
                        name="schatzmeisterRole"
                        value={treasurer.schatzmeisterRole}
                        onChange={handleTreasurerInputChange}
                    />
                    <label>Straße:</label>
                    <input
                        type="text"
                        name="schatzmeisterStreet"
                        value={treasurer.schatzmeisterStreet}
                        onChange={handleTreasurerInputChange}
                    />
                    <label>PLZ:</label>
                    <input
                        type="text"
                        name="schatzmeisterPostal"
                        value={treasurer.schatzmeisterPostal}
                        onChange={handleTreasurerInputChange}
                    />
                    <label>Stadt:</label>
                    <input
                        type="text"
                        name="schatzmeisterCity"
                        value={treasurer.schatzmeisterCity}
                        onChange={handleTreasurerInputChange}
                    />
                    <label>E-Mail:</label>
                    <input
                        type="email"
                        name="schatzmeisterEmail"
                        value={treasurer.schatzmeisterEmail}
                        onChange={handleTreasurerInputChange}
                    />
                    <label>Telefon:</label>
                    <input
                        type="tel"
                        name="schatzmeisterPhone"
                        value={treasurer.schatzmeisterPhone}
                        onChange={handleTreasurerInputChange}
                    />
                </div>
                <div className="profile-actions">
                    <button
                        className="save-button"
                        onClick={handleTreasurerSave}
                        disabled={!isTreasurerChanged || !treasurer.orgName.trim() || !treasurer.schatzmeisterName.trim()}
                    >
                        Speichern
                    </button>
                    <button
                        className="cancel-button"
                        onClick={handleTreasurerCancel}
                        disabled={!isTreasurerChanged}
                    >
                        Abbrechen
                    </button>
                </div>
                    </>
                )}
            </div>

            {/* Bank Accounts List */}
            <div className="profile-card">
                <div className="bank-accounts-header">
                    <h2>Bankkonten</h2>
                    <button className="add-account-button" onClick={handleAddAccount}>
                        + Konto hinzufügen
                    </button>
                </div>

                <div className="bank-accounts-list">
                    {/* Table Header */}
                    <div className="bank-account-row header">
                        <div className="account-column">Kontoname</div>
                        <div className="account-column">Institut</div>
                        <div className="account-column">IBAN</div>
                        <div className="account-column">BIC</div>
                        <div className="account-column">Gläubiger-ID</div>
                        <div className="account-column actions">Aktionen</div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            Lade Bankkonten...
                        </div>
                    )}

                    {/* Existing Accounts */}
                    {!loading && bankAccounts.map((account) => (
                        <div key={account.id} className="bank-account-row">
                            {editingAccountId === account.id ? (
                                <>
                                    <div className="account-column">
                                        <input
                                            type="text"
                                            value={account.accountName}
                                            onChange={(e) =>
                                                handleAccountInputChange(account.id, 'accountName', e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="account-column">
                                        <input
                                            type="text"
                                            value={account.institute}
                                            onChange={(e) =>
                                                handleAccountInputChange(account.id, 'institute', e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="account-column">
                                        <input
                                            type="text"
                                            value={account.iban}
                                            onChange={(e) =>
                                                handleAccountInputChange(account.id, 'iban', e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="account-column">
                                        <input
                                            type="text"
                                            value={account.bic}
                                            onChange={(e) =>
                                                handleAccountInputChange(account.id, 'bic', e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="account-column">
                                        <input
                                            type="text"
                                            value={account.glaeubigerId}
                                            onChange={(e) =>
                                                handleAccountInputChange(account.id, 'glaeubigerId', e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="account-column actions">
                                        <button
                                            className="save-button-small"
                                            onClick={() => handleSaveAccount(account.id)}
                                        >
                                            ✓
                                        </button>
                                        <button
                                            className="cancel-button-small"
                                            onClick={() => handleCancelEditAccount(account.id)}
                                        >
                                            ✗
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="account-column">{account.accountName}</div>
                                    <div className="account-column">{account.institute}</div>
                                    <div className="account-column">{account.iban}</div>
                                    <div className="account-column">{account.bic}</div>
                                    <div className="account-column">{account.glaeubigerId}</div>
                                    <div className="account-column actions">
                                        <button
                                            className="edit-button"
                                            onClick={() => handleEditAccount(account.id)}
                                        >
                                            ✏️ Bearbeiten
                                        </button>
                                        <button
                                            className="delete-button"
                                            onClick={() => handleDeleteAccount(account.id)}
                                        >
                                            🗑️ Löschen
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}

                    {/* Add New Account Row */}
                    {isAddingAccount && (
                        <div className="bank-account-row adding">
                            <div className="account-column">
                                <input
                                    type="text"
                                    placeholder="Kontoname"
                                    value={newAccount.accountName}
                                    onChange={(e) =>
                                        handleNewAccountInputChange('accountName', e.target.value)
                                    }
                                />
                            </div>
                            <div className="account-column">
                                <input
                                    type="text"
                                    placeholder="Institut"
                                    value={newAccount.institute}
                                    onChange={(e) =>
                                        handleNewAccountInputChange('institute', e.target.value)
                                    }
                                />
                            </div>
                            <div className="account-column">
                                <input
                                    type="text"
                                    placeholder="IBAN"
                                    value={newAccount.iban}
                                    onChange={(e) =>
                                        handleNewAccountInputChange('iban', e.target.value)
                                    }
                                />
                            </div>
                            <div className="account-column">
                                <input
                                    type="text"
                                    placeholder="BIC"
                                    value={newAccount.bic}
                                    onChange={(e) =>
                                        handleNewAccountInputChange('bic', e.target.value)
                                    }
                                />
                            </div>
                            <div className="account-column">
                                <input
                                    type="text"
                                    placeholder="Gläubiger-ID"
                                    value={newAccount.glaeubigerId}
                                    onChange={(e) =>
                                        handleNewAccountInputChange('glaeubigerId', e.target.value)
                                    }
                                />
                            </div>
                            <div className="account-column actions">
                                <button
                                    className="save-button-small"
                                    onClick={handleSaveNewAccount}
                                >
                                    ✓
                                </button>
                                <button
                                    className="cancel-button-small"
                                    onClick={handleCancelAddAccount}
                                >
                                    ✗
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoundTableProfile;
